import { useEffect, useState } from 'react';
import { apiRequest } from './client';

// Bus tickets in the API's own format (see /api/bus in the backend README).

/** City and stop suggestions for the From / To boxes: [{ name }]. Pass name as the search source / destination. */
export const searchStations = (query, signal) =>
  apiRequest(`/api/bus/stations?query=${encodeURIComponent(query)}`, { signal });

export const searchBuses = (request, signal) =>
  apiRequest('/api/bus/search', { body: request, signal });

/** Live seat map (decks), boarding and dropping points for one searched bus. */
export const getSeatMap = (searchId, busId, signal) =>
  apiRequest('/api/bus/seats', { body: { searchId, busId }, signal });

/** Re-checks the chosen seats and stops. Returns a reviewId for checkout, valid for 10 minutes. */
export const reviewBus = (request, signal) =>
  apiRequest('/api/bus/review', { body: request, signal });

/** Guest or signed-in booking of reviewed seats; links it to an account by email like flight checkout. */
export const checkoutBus = (request, token) =>
  apiRequest('/api/bus/checkout', { body: request, token });

/** Live ticket status, operator PNR and passengers. Guests pass the booking email. */
export const getBusBooking = (supplierBookingId, email, token, signal) =>
  apiRequest('/api/bus/bookings/details', { body: { supplierBookingId, email: email || null }, token, signal });

/** Operator charges and refund for cancelling now. Requires sign-in. */
export const quoteBusCancellation = (supplierBookingId, token) =>
  apiRequest('/api/bus/bookings/cancellation/quote', { body: { supplierBookingId }, token });

export const cancelBus = (supplierBookingId, cancellationId, token) =>
  apiRequest('/api/bus/bookings/cancellation', { body: { supplierBookingId, cancellationId }, token });

// Searches by URL, so moving between results and the seat map doesn't search again within the search's lifetime.
const searchCache = new Map();

/** This browser's results for a search, while the API still holds them (e.g. the seat map page after a refresh). */
export function cachedBusSearch(search) {
  const cached = searchCache.get(JSON.stringify(search));
  return cached && new Date(cached.expiresAt) > Date.now() ? cached : null;
}

/** Searches whenever the search changes; `retry` searches again. `toRequest(search)` builds the API request. */
export function useBusSearch(search, toRequest) {
  const key = search ? JSON.stringify(search) : '';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: Boolean(search), data: null, error: '' });

  useEffect(() => {
    if (!key) return undefined;
    const cached = searchCache.get(key);
    if (cached && attempt === 0 && new Date(cached.expiresAt) - Date.now() > 60e3) {
      setState({ loading: false, data: cached, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState({ loading: true, data: null, error: '' });
    searchBuses(toRequest(JSON.parse(key)), controller.signal)
      .then((data) => {
        searchCache.set(key, data);
        setState({ loading: false, data, error: '' });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ loading: false, data: null, error: error.message || 'We could not load buses for this route.' });
      });
    return () => controller.abort();
    // toRequest is a pure function of the search, which is already in the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}
